/**
 * File Dropzone Component
 * Drag-and-drop file upload for S3-triggered networks
 */
import React, { useState, useCallback } from 'react';
import { getRestApiBearerToken } from '@/services/apiToken';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { useAppStore } from '@/stores/useAppStore';
import { attachmentNeedsTextract } from '@/utils/runnerAttachments';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

export default function FileDropzone({
  networkId,
  runtimeConfig,
  onUploadSuccess,
  /** Called after S3 PUT succeeds with metadata for Run-tab execute (optional). */
  onUploadedAsset,
  compact = false,
  /** Compact mode: allow multi-select and upload all in sequence (Run tab attachments). */
  compactMulti = false,
  /** Relative path under inputPrefix (e.g. AgentId/ or AgentId/subfolder/) */
  currentPrefix = '',
  /** When set, uploads go to input/logs/attachments/tmp/{stagingId}/ (Run tab; moved on execute). */
  stagingId = ''
}) {
  const { user } = useAuth();
  const { setRunnerStatus, addRunnerLog, setRunnerProgress } = useAppStore();
  
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (!files?.length) return;
      if (compact && compactMulti) {
        setSelectedFile(Array.from(files));
      } else if (files[0]) {
        setSelectedFile(files[0]);
      }
    },
    [compact, compactMulti]
  );

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    if (compact && compactMulti) {
      setSelectedFile(Array.from(files));
    } else if (files[0]) {
      setSelectedFile(files[0]);
    }
  };

  const uploadOneFile = async (file) => {
    const contentType = file.type || 'application/octet-stream';
    addRunnerLog('🔗 Requesting upload URL from API...');
    const idToken = getRestApiBearerToken();

    const urlParams = new URLSearchParams({
      filename: file.name,
      contentType
    });
    if (stagingId) {
      urlParams.set('stagingId', stagingId);
    } else if (currentPrefix) {
      urlParams.set('prefix', currentPrefix);
    }

    const urlResponse = await fetch(
      `${API_BASE_URL}/networks/${networkId}/upload-url?${urlParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!urlResponse.ok) {
      throw new Error(`Failed to get upload URL: ${urlResponse.statusText}`);
    }

    const { uploadUrl, s3Key, bucket, requiredHeaders } = await urlResponse.json();
    addRunnerLog(`✅ Upload URL received`);
    addRunnerLog(`📍 Target: s3://${bucket || 'bucket'}/${s3Key}`);

    addRunnerLog('📤 Uploading file to S3...');
    setRunnerProgress(0);

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        setRunnerProgress(percent);
      }
    });

    await new Promise((resolve, reject) => {
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.statusText}`));
        }
      };
      xhr.onerror = () => reject(new Error('Network error during upload'));

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      // Any extra headers the presign signed (e.g. x-amz-tagging on staging
      // uploads, so the orphaned-staging lifecycle rule can target them). Must
      // match exactly or S3 returns 403.
      Object.entries(requiredHeaders || {}).forEach(([k, v]) => {
        if (k && v != null) xhr.setRequestHeader(k, String(v));
      });
      xhr.send(file);
    });

    setRunnerProgress(100);
    addRunnerLog(`✅ File uploaded successfully to S3`);

    const needsProcessing = attachmentNeedsTextract(contentType);
    onUploadedAsset?.({
      filename: file.name,
      contentType,
      bucket: bucket || '',
      s3Key,
      size: file.size,
      needs_processing: needsProcessing
    });
  };

  const handleUpload = async () => {
    const queue =
      compact && compactMulti && Array.isArray(selectedFile)
        ? selectedFile
        : selectedFile
          ? [selectedFile]
          : [];
    if (!queue.length) return;

    setUploading(true);
    setUploadSuccess(false);
    setRunnerStatus('uploading');
    addRunnerLog(
      queue.length > 1
        ? `📤 Preparing upload: ${queue.length} files`
        : `📤 Preparing upload: ${queue[0].name}`
    );

    try {
      for (let i = 0; i < queue.length; i += 1) {
        const file = queue[i];
        if (queue.length > 1) {
          addRunnerLog(`📎 Uploading ${i + 1}/${queue.length}: ${file.name}`);
        }
        await uploadOneFile(file);
      }

      setUploadSuccess(true);
      setRunnerStatus('completed');
      onUploadSuccess?.();

      setTimeout(() => {
        setSelectedFile(null);
        setUploadSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Upload error:', error);
      addRunnerLog(`❌ Upload failed: ${error.message}`);
      setRunnerStatus('error');
    } finally {
      setUploading(false);
    }
  };

  const compactHasSelection = Boolean(
    selectedFile && (Array.isArray(selectedFile) ? selectedFile.length > 0 : true)
  );
  const compactSummary =
    !selectedFile
      ? 'Drop file or click to browse'
      : Array.isArray(selectedFile)
        ? selectedFile.length === 1
          ? selectedFile[0].name
          : `${selectedFile.length} files selected`
        : selectedFile.name;

  if (compact) {
    return (
      <div className="flex items-center gap-3">
        <label
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            flex-1 flex items-center gap-2 px-4 py-2.5 border-2 border-dashed rounded-lg cursor-pointer transition-all text-sm
            ${isDragging ? 'border-blue-500 bg-blue-50' : compactHasSelection ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}
          `}
        >
          <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <span className="text-gray-600 truncate" title={compactSummary}>
            {compactSummary}
          </span>
          <input
            type="file"
            multiple={Boolean(compactMulti)}
            onChange={handleFileSelect}
            className="hidden"
          />
        </label>
        {compactHasSelection && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSelectedFile(null)}
              disabled={uploading}
              className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpload}
              disabled={uploading}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              {uploading ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" /></svg>
              )}
              Upload
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Dropzone */}
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          flex-1 border-2 border-dashed rounded-2xl transition-all cursor-pointer
          ${isDragging
            ? 'border-blue-500 bg-blue-50'
            : selectedFile
            ? 'border-green-500 bg-green-50'
            : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
          }
        `}
      >
        <div className="h-full flex flex-col items-center justify-center p-8 text-center">
          <AnimatePresence mode="wait">
            {uploadSuccess ? (
              <motion.div
                key="success"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className="flex flex-col items-center"
              >
                <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-green-700">Upload Successful!</h3>
                <p className="text-sm text-green-600 mt-2">Network will be triggered automatically</p>
              </motion.div>
            ) : selectedFile ? (
              <motion.div
                key="selected"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center w-full"
              >
                {/* File Icon */}
                <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mb-4">
                  <svg className="w-10 h-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>

                {/* File Info */}
                <div className="mb-6 w-full max-w-md">
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <p className="font-medium text-gray-800 truncate">{selectedFile.name}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setSelectedFile(null)}
                    disabled={uploading}
                    className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={uploading}
                    className="px-8 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all shadow-sm font-medium disabled:opacity-50 flex items-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
                        </svg>
                        Upload & Trigger
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center"
              >
                {/* Upload Icon */}
                <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-6 transition-colors ${
                  isDragging ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  <svg
                    className={`w-12 h-12 transition-colors ${
                      isDragging ? 'text-blue-500' : 'text-gray-400'
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>

                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {isDragging ? 'Drop your file here' : 'Drop file to upload'}
                </h3>
                <p className="text-gray-600 mb-6">
                  or click to browse files
                </p>

                {/* Browse Button */}
                <label className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all shadow-sm font-medium cursor-pointer">
                  Browse Files
                  <input
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>

                {/* Info */}
                <div className="mt-8 text-sm text-gray-500">
                  <p>Upload location:</p>
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded mt-1 inline-block break-all">
                    {runtimeConfig?.inputPrefix
                      ? `${runtimeConfig.inputPrefix}${currentPrefix || ''}`
                      : '—'}
                  </code>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
